import { Grid, Hidden, Typography } from '@material-ui/core';
import { navigate } from '@reach/router';
import React from 'react';
import useFormState from '../../../hooks/useFormState';
import { useTranslation } from '../../../hooks/useTranslation';
import ProceedOrCancel from '../../proceed-or-cancel';
import { WizardStepProps } from './wizard.types';

const createUnityFormAdapter = <T, U = T>() => ({
  toFormState: (initial: T): U => (initial as unknown) as U,
  fromFormState: (state: U): T => (state as unknown) as T,
});

export function WizardStep<T, U = T>({
  initial,
  title,
  nextRoute,
  onNext,
  backRoute,
  onBack,
  backText,
  nextText,
  component,
  nextButton,
  validate,
  formAdapter = createUnityFormAdapter<T, U>(),
  componentProps,
  proceedDisabled = false
}: WizardStepProps<T, U>) {
  const { t } = useTranslation();
  const formState = useFormState(formAdapter.toFormState(initial), {
    validate,
  });

  const backLabel = backText ?? t('BACK');
  const nextLabel = nextText ?? t('NEXT');

  const showNextButton = nextButton ?? true;

  const handleSubmit = async (state: U) => {
    const ok = await onNext(formAdapter.fromFormState(state));
    if (ok && nextRoute) {
      await navigate(nextRoute);
    }
  };

  return (
    <Grid container direction="column" spacing={4}>
      <Grid item container spacing={2}>
        {title && (
          <Hidden smUp>
            <Grid item container>
              <Typography variant={'h1'} align={'center'}>
                {title}
              </Typography>
            </Grid>
          </Hidden>
        )}
        <Grid item container>
          {component({ formState, initial, componentProps })}
        </Grid>
      </Grid>
      {showNextButton && (
        <Grid item>
          <ProceedOrCancel
            cancelRoute={backRoute ?? false}
            cancelHandler={onBack}
            cancelVariant={'Button'}
            proceedHandler={formState.submit(
              async values => await handleSubmit(values),
              console.log,
            )}
            proceedLabel={nextLabel}
            cancelLabel={backLabel}
            proceedDisabled={proceedDisabled}
          />
        </Grid>
      )}
    </Grid>
  );
}
